/**
 * "Sign in with Ordinal Pass" — browser half of an OIDC authorization-code
 * flow with PKCE (S256) against the Qodoonect Trust OIDC provider.
 *
 * The browser never sees a client secret or a refresh token:
 *   1. beginLogin()   — make state + nonce + PKCE verifier, keep them in
 *                       sessionStorage for one round trip, go to Trust's
 *                       /oidc/authorize.
 *   2. /auth/callback — readCallback() checks `state` against the stored
 *                       transaction (one use, 10 min), then the code goes to
 *                       this app's own backend (/api/auth/token, api/_lib/trust.ts),
 *                       which holds the client secret, redeems the code with the
 *                       verifier, verifies the id_token (signature, iss, aud,
 *                       nonce) and keeps the refresh token in an HttpOnly cookie.
 *
 * Off unless VITE_TRUST_ISSUER and VITE_TRUST_CLIENT_ID are set at build time.
 * Pure apart from the injected storage / crypto, so it is unit-tested with
 * `node --test` (tests/oidc.test.mjs).
 */

export interface TrustConfig {
  /** Expected `iss` of Trust tokens, e.g. https://trust.qodoonect.com */
  issuer: string;
  clientId: string;
  /** Browser-reachable authorize endpoint (defaults to <issuer>/oidc/authorize). */
  authorizeUrl: string;
  scope: string;
}

export interface ReturnTo {
  from?: string;
  template?: string;
  /** A rune handoff was in progress (route.ts handoff): come back to it and ask the rune again. */
  handoff?: boolean;
}

/** What survives the round trip to Trust (sessionStorage, one use). */
export interface LoginTransaction {
  state: string;
  nonce: string;
  verifier: string;
  redirectUri: string;
  returnTo: ReturnTo;
  createdAt: number;
}

export type CallbackResult =
  | { ok: true; code: string; tx: LoginTransaction }
  | { ok: false; error: string; message: string; returnTo?: ReturnTo };

export const TX_STORAGE_KEY = 'collect-oidc-tx';
export const TX_MAX_AGE_MS = 10 * 60 * 1000;
export const CALLBACK_PATH = '/auth/callback';
export const DEFAULT_SCOPE = 'openid workspace';

type Env = Record<string, string | boolean | undefined>;

function readEnv(): Env {
  try {
    return ((import.meta as any).env || {}) as Env;
  } catch {
    return {};
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Vite dev server (import.meta.env.DEV) — the only place loopback http is allowed. */
export function isDevBuild(env: Env = readEnv()): boolean {
  return env.DEV === true;
}

/**
 * Trust settings from VITE_TRUST_* (build time), or null when sign-in with
 * Ordinal Pass is off. https only; a loopback http issuer only in a dev build.
 */
export function trustConfig(env: Env = readEnv()): TrustConfig | null {
  const issuer = str(env.VITE_TRUST_ISSUER).replace(/\/+$/, '');
  const clientId = str(env.VITE_TRUST_CLIENT_ID);
  if (!issuer || !clientId) return null;
  const authorizeUrl = str(env.VITE_TRUST_AUTHORIZE_URL) || `${issuer}/oidc/authorize`;
  const dev = isDevBuild(env);
  if (!isAllowedEndpoint(issuer, dev) || !isAllowedEndpoint(authorizeUrl, dev)) return null;
  return { issuer, clientId, authorizeUrl, scope: str(env.VITE_TRUST_SCOPE) || DEFAULT_SCOPE };
}

/**
 * Access-key sign-in (closed beta). VITE_ACCESS_KEY_LOGIN=on|off decides;
 * unset, it is on only while sign-in with Ordinal Pass is not configured.
 */
export function accessKeyLoginEnabled(env: Env = readEnv()): boolean {
  const v = str(env.VITE_ACCESS_KEY_LOGIN).toLowerCase();
  if (v === 'on') return true;
  if (v === 'off') return false;
  return trustConfig(env) === null;
}

const LOOPBACK = /^(localhost|127\.0\.0\.1|\[::1\])$/i;

export function isAllowedEndpoint(raw: string, allowLoopback = false): boolean {
  try {
    const u = new URL(raw);
    if (u.username || u.password || u.hash) return false;
    return u.protocol === 'https:' || (allowLoopback && u.protocol === 'http:' && LOOPBACK.test(u.hostname));
  } catch {
    return false;
  }
}

/**
 * Other origins that may receive the Trust token. None by default: Collect's own
 * server is reached on this site's origin (/api/collect/* proxied by vercel.json),
 * which apiOriginAllowed always allows. A self-hosted server gets the token only
 * when its origin is listed in VITE_COLLECT_API_ORIGINS.
 */
export const DEFAULT_API_ORIGINS: string[] = [];

/** Origins that may receive the Trust token: VITE_COLLECT_API_ORIGINS (comma-separated) or the default. */
export function allowedApiOrigins(env: Env = readEnv()): string[] {
  const listed = str(env.VITE_COLLECT_API_ORIGINS);
  return listed ? listed.split(',').map((o) => o.trim().replace(/\/+$/, '')).filter(Boolean) : DEFAULT_API_ORIGINS;
}

// A same-origin path prefix (reverse proxy): plain segments only — no "%",
// no "\\", no control characters, which the URL parser would rewrite.
const SAME_ORIGIN_PATH = /^(\/[A-Za-z0-9._~-]+)+$/;

/**
 * May the Trust access token go with requests to this backend base? The base
 * is the normalized server address (Settings → Advanced; '' by default) that
 * CollectClient prefixes to every /api/collect/* path, so it is judged by the
 * URL fetch() will really use: refused when it holds control characters or a
 * backslash (the URL parser strips or rewrites them: "/\\evil.example" →
 * //evil.example); allowed for '' (same origin — the Vercel rewrite, or the
 * Vite proxy in dev) or a plain same-origin path;
 * otherwise only when `new URL(base + '/api/collect/health', page).origin`
 * is this site or one of allowedApiOrigins(). The single gate for the token
 * (CollectClient.authHeaders).
 */
export function apiOriginAllowed(base: string, pageOrigin: string, env: Env = readEnv()): boolean {
  if (typeof base !== 'string' || /[\u0000-\u001F\u007F\\]/.test(base)) return false;
  const b = base.trim();
  if (b === '') return true;
  if (b.startsWith('/')) {
    if (!SAME_ORIGIN_PATH.test(b)) return false;
  } else if (!/^https?:\/\//i.test(b)) {
    return false; // relative or other-scheme bases are not API backends
  }
  let origin: string;
  try { origin = new URL(`${b}/api/collect/health`, pageOrigin).origin; } catch { return false; }
  if (origin === 'null') return false;
  if (origin === pageOrigin) return true;
  return allowedApiOrigins(env).includes(origin);
}

// ─── random values and PKCE ─────────────────────────────────────────────────

export function base64url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes → 43 base64url chars (RFC 7636 §4.1 verifier length 43–128). */
export function randomToken(cryptoImpl: Crypto = globalThis.crypto): string {
  const bytes = new Uint8Array(32);
  cryptoImpl.getRandomValues(bytes);
  return base64url(bytes);
}

/** code_challenge = BASE64URL(SHA-256(ASCII(code_verifier))) — RFC 7636 §4.2, S256. */
export async function pkceChallenge(verifier: string, cryptoImpl: Crypto = globalThis.crypto): Promise<string> {
  const digest = await cryptoImpl.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

// ─── authorize request ──────────────────────────────────────────────────────

export function redirectUriFor(origin: string): string {
  return `${origin.replace(/\/+$/, '')}${CALLBACK_PATH}`;
}

export async function createTransaction(
  origin: string,
  returnTo: ReturnTo,
  now: number = Date.now(),
  cryptoImpl: Crypto = globalThis.crypto,
): Promise<{ tx: LoginTransaction; challenge: string }> {
  const tx: LoginTransaction = {
    state: randomToken(cryptoImpl),
    nonce: randomToken(cryptoImpl),
    verifier: randomToken(cryptoImpl),
    redirectUri: redirectUriFor(origin),
    returnTo: {
      ...(returnTo.from ? { from: returnTo.from } : {}),
      ...(returnTo.template ? { template: returnTo.template } : {}),
      ...(returnTo.template && returnTo.handoff === true ? { handoff: true } : {}),
    },
    createdAt: now,
  };
  return { tx, challenge: await pkceChallenge(tx.verifier, cryptoImpl) };
}

export function buildAuthorizeUrl(cfg: TrustConfig, tx: LoginTransaction, challenge: string): string {
  const u = new URL(cfg.authorizeUrl);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('client_id', cfg.clientId);
  u.searchParams.set('redirect_uri', tx.redirectUri);
  u.searchParams.set('scope', cfg.scope);
  u.searchParams.set('state', tx.state);
  u.searchParams.set('nonce', tx.nonce);
  u.searchParams.set('code_challenge', challenge);
  u.searchParams.set('code_challenge_method', 'S256');
  return u.toString();
}

// ─── transaction storage (sessionStorage: this tab, this round trip) ────────

type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function saveTransaction(store: KV, tx: LoginTransaction): void {
  store.setItem(TX_STORAGE_KEY, JSON.stringify(tx));
}

/** Read and delete: a transaction is good for exactly one callback. */
export function takeTransaction(store: KV): LoginTransaction | null {
  let raw: string | null = null;
  try {
    raw = store.getItem(TX_STORAGE_KEY);
  } finally {
    store.removeItem(TX_STORAGE_KEY);
  }
  if (!raw) return null;
  try {
    const t = JSON.parse(raw);
    if (
      t && typeof t.state === 'string' && typeof t.nonce === 'string' && typeof t.verifier === 'string'
      && typeof t.redirectUri === 'string' && typeof t.createdAt === 'number'
    ) {
      return { ...t, returnTo: t.returnTo && typeof t.returnTo === 'object' ? t.returnTo : {} };
    }
  } catch {
    /* fall through */
  }
  return null;
}

/** Length-independent comparison of two ASCII strings. */
export function sameString(a: string, b: string): boolean {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  let diff = a.length ^ b.length;
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) diff |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0);
  return diff === 0;
}

const GENERIC_FAILURE = 'Sign-in did not complete. Start again.';

/**
 * Validate /auth/callback?code=…&state=… against the stored transaction.
 * `tx` must already be taken out of storage (takeTransaction), so a replayed
 * callback URL finds nothing. Anyone can craft a callback URL, so nothing from
 * it is ever shown: without a matching pending transaction the message is
 * generic, and with one only a fixed text per known error code is used —
 * `error_description` is never echoed.
 */
export function readCallback(search: string, tx: LoginTransaction | null, now: number = Date.now()): CallbackResult {
  const p = new URLSearchParams(search || '');
  const state = p.get('state') || '';
  const stateOk = Boolean(tx && state && sameString(state, tx.state));
  const error = p.get('error');
  if (error) {
    if (!stateOk) return { ok: false, error: 'authorization_error', message: GENERIC_FAILURE };
    const code = error === 'access_denied' ? 'access_denied' : 'authorization_error';
    return {
      ok: false,
      error: code,
      message: code === 'access_denied' ? 'Sign-in was cancelled at Qodoonect Trust.' : 'Qodoonect Trust did not sign you in. Start again.',
      returnTo: tx?.returnTo,
    };
  }
  if (!tx) {
    return { ok: false, error: 'no_transaction', message: 'This sign-in link was already used or did not start here. Start again.' };
  }
  if (!stateOk) {
    return { ok: false, error: 'state_mismatch', message: 'The sign-in answer does not match this browser tab. Start again.', returnTo: tx.returnTo };
  }
  if (now - tx.createdAt > TX_MAX_AGE_MS || now < tx.createdAt) {
    return { ok: false, error: 'expired', message: 'The sign-in took longer than 10 minutes. Start again.', returnTo: tx.returnTo };
  }
  const code = p.get('code') || '';
  if (!code || code.length > 512) {
    return { ok: false, error: 'no_code', message: 'Trust did not return an authorization code.', returnTo: tx.returnTo };
  }
  return { ok: true, code, tx };
}

// ─── browser entry point ────────────────────────────────────────────────────

/** Store the transaction in sessionStorage and leave for Trust's authorize page. */
export async function startLogin(cfg: TrustConfig, returnTo: ReturnTo): Promise<void> {
  const { tx, challenge } = await createTransaction(window.location.origin, returnTo);
  saveTransaction(window.sessionStorage, tx);
  window.location.assign(buildAuthorizeUrl(cfg, tx, challenge));
}
